import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getPageReturn,
  clearPageReturn,
  subscribeToPageReturn,
} from "../../utils/pageReturn";
import "./PageReturnPill.css";

/**
 * The mirror of PersonReturnPill: that one shows on a study page and points
 * back to a profile, this one shows on a profile and points back to the study
 * page (and, where the page supports it, the modal) the reader came from.
 *
 * Mounted once for the whole app so the 2,600-line profile page does not have
 * to know about it, and so it cannot be missed by a page that forgets to render
 * it.
 */
export default function PageReturnPill() {
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;
  const [entry, setEntry] = useState(null);

  /*
   * Whether the way back has actually been offered yet.
   *
   * Without this the hop deletes itself the instant it is made: saving emits a
   * change event, this component re-syncs on it while the reader is still on
   * the origin page, and "already on the origin route" reads as a completed
   * round trip. The pill survived on router state alone, so the bug only
   * showed as a refresh on the profile losing the way back.
   */
  const offered = useRef(false);

  const sync = useCallback(() => {
    const stateEntry = location.state?.pageReturn;
    const saved = stateEntry?.route ? stateEntry : getPageReturn();

    if (!saved?.route) {
      setEntry(null);
      return;
    }

    // Back on the page that sent them -- the round trip is over, but only if
    // they were ever given the way back.
    if (pathname === saved.route) {
      if (offered.current) {
        clearPageReturn();
        offered.current = false;
      }

      setEntry(null);
      return;
    }

    // Only offered on a profile. A reader who has wandered somewhere else has
    // left this hop behind, and a pill that followed them around would be
    // pointing at a page they no longer came from.
    const onProfile = pathname.startsWith("/people/");
    if (onProfile) offered.current = true;

    setEntry(onProfile ? saved : null);
  }, [pathname, location.state]);

  useEffect(() => {
    sync();
  }, [sync]);

  useEffect(() => subscribeToPageReturn(sync), [sync]);

  /*
   * Forgetting has to reset the ref as well as the hop. This component never
   * unmounts, so a stale `offered` from a completed round trip would make the
   * reader's *next* hop delete itself the moment it was saved.
   */
  const forget = useCallback(() => {
    clearPageReturn();
    offered.current = false;
    setEntry(null);
  }, []);

  if (!entry) return null;

  const label = entry.label || "the page you were reading";

  /*
   * Navigated rather than linked, because going back has to carry the reopen
   * instruction in router state -- that is what lets an explorer put its modal
   * back the way the reader left it.
   */
  const goBack = () => {
    forget();
    navigate(entry.route, {
      state: entry.reopen ? { reopenPerson: entry.reopen } : undefined,
    });
  };

  return (
    <div className="ctb-pageReturnPill" data-testid="page-return-pill">
      <button type="button" className="ctb-pageReturnPillLink" onClick={goBack}>
        <span className="ctb-pageReturnPillArrow" aria-hidden="true">
          ←
        </span>
        <span className="ctb-pageReturnPillCopy">
          <span className="ctb-pageReturnPillEyebrow">Back to</span>
          <span className="ctb-pageReturnPillName">{label}</span>
        </span>
      </button>

      <button
        type="button"
        className="ctb-pageReturnPillDismiss"
        aria-label={`Dismiss return link to ${label}`}
        onClick={forget}
      >
        ×
      </button>
    </div>
  );
}
